import type { AffectedMachineReason, DataState, RevenueMachineCompleteness } from '../../api/revenue';

// Shared rendering helpers for the revenue dashboard pages (portfolio /
// site / machine — tasks 9-11). Kept separate from utils/format.ts because
// these are revenue-domain-specific (data-state/completeness semantics),
// not generic value formatters.

/** MUI Chip/`sx.color` tone used across the revenue chip helpers. */
export type RevenueChipTone = 'default' | 'success' | 'warning' | 'error' | 'info';

/**
 * Net win colour (an `sx.color` theme path, NOT a Typography `color` prop
 * value — those two use different keyword sets and 'text.primary' is only
 * valid for the former). Negative net win — the operator paid out more
 * than it took in over the period — renders as the error red so it reads
 * as a loss at a glance; zero or positive is left `undefined` so it
 * inherits the surrounding text colour.
 */
export function netWinColor(cents: number | null | undefined): string | undefined {
  return cents != null && cents < 0 ? 'error.main' : undefined;
}

/**
 * Page/row-level data-state chip. `currentPeriod` always implies
 * `provisional` (today's figures are still accumulating — see
 * RevenueReportService: `provisional = period.IsCurrent || affected.Count
 * > 0`); a *historical* period is only provisional when it has machines
 * flagged for review. Under the include-and-flag model those machines are
 * NOT excluded from the totals — their suspect readings are counted and
 * marked for later review. Spec §5.2 F-4: never let a historical table
 * read as if it were live, or vice versa — these three states are
 * mutually exclusive and always describe exactly what the numbers on
 * screen mean.
 */
export function dataStateChip(ds: DataState): { label: string; color: RevenueChipTone } {
  if (ds.currentPeriod) return { label: 'Live — today', color: 'info' };
  if (ds.provisional) return { label: `Provisional (${ds.affectedCount} flagged)`, color: 'warning' };
  return { label: 'Final', color: 'success' };
}

/** Per-machine completeness chip — machine drill-down (task 11). */
export function completenessChip(state: RevenueMachineCompleteness): { label: string; color: RevenueChipTone } {
  switch (state) {
    case 'complete': return { label: 'Complete', color: 'success' };
    case 'incomplete': return { label: 'Incomplete', color: 'warning' };
    case 'unavailable': return { label: 'Unavailable', color: 'default' };
    default: return { label: state, color: 'default' };
  }
}

/**
 * The "vs prior <period>" label shown under a KPI delta. Maps a period token to
 * the human phrase describing the immediately-preceding window of the same
 * length (see RevenueReportService.PriorBounds).
 */
export function priorPeriodLabel(token: string): string {
  switch (token) {
    case 'today': return 'vs yesterday';
    case 'yesterday': return 'vs prior day';
    case '7d': return 'vs prior 7 days';
    case '30d': return 'vs prior 30 days';
    case '90d': return 'vs prior 90 days';
    case 'mtd': return 'vs prior month-to-date';
    case 'custom': return 'vs prior period';
    default: return 'vs prior period';
  }
}

/**
 * A vs-prior-period delta computed from a current and prior value (cents, or any
 * comparable magnitude). Returns null when there is no valid comparison — no
 * prior value (null) or a zero prior base (division by zero / undefined growth).
 * `pct` is the signed percentage change `(current - prior) / prior * 100`;
 * `direction` is 'up' | 'down' | 'flat' for the ▲/▼ marker + colour.
 */
export type RevenueDelta = { pct: number; direction: 'up' | 'down' | 'flat' };

export function revenueDelta(current: number, prior: number | null | undefined): RevenueDelta | null {
  if (prior == null || prior === 0) return null;
  const pct = ((current - prior) / prior) * 100;
  const direction = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  return { pct, direction };
}

/**
 * Per-site connectivity status derived from online/total machine counts
 * (current-period only — the caller must gate on dataState.currentPeriod and
 * pass null otherwise). All online → Online (green); some offline → Degraded
 * (amber); none online → Offline (red). Null when counts are unavailable
 * (historical period, or no machines) → the caller renders an em-dash.
 */
export function siteStatus(
  online: number | null, total: number | null,
): { label: string; color: RevenueChipTone } | null {
  if (online == null || total == null || total === 0) return null;
  if (online === total) return { label: 'Online', color: 'success' };
  if (online === 0) return { label: 'Offline', color: 'error' };
  return { label: 'Degraded', color: 'warning' };
}

/** Human-readable label for a flagged-machine reason code (informational —
 *  the machine is included in the totals, not excluded). */
export function affectedReasonLabel(reason: AffectedMachineReason): string {
  switch (reason) {
    case 'baseline_day': return 'Baseline day (no prior meter to diff against)';
    case 'suspect_day': return 'Suspect reading (flagged for review)';
    case 'unreviewed_version': return 'Unreviewed version reset';
    case 'unavailable': return 'Unavailable';
    default: return reason;
  }
}
